import React, { useState, useEffect } from 'react';
import { Modal, InputNumber, Button, message } from 'antd';
import { PlusOutlined, MinusCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import { getClientType } from '../../../tools/session/clientSession';
import '../tlgreenwave.css';

/**
 * ConfigureSemaforiModal Component
 * Modal for configuring traffic light parameters before calculating green wave offset.
 *
 * Visible fields per traffic light:
 *   Nome, Inizio Verde, Durata Verde, Durata Giallo, Durata Rosso, Lunghezza Attraversamento
 *
 * Link pairs section (consecutive traffic lights):
 *   Distanza [m], Velocità [km/h]
 *
 * API request body is NOT changed – hidden fields are sent with their default values.
 */
function ConfigureSemaforiModal({
  visible,
  onClose,
  selectedLights,
  trafficLights,
  onCalculate,
  isLoading,
  // Persistence props – lifted state from parent
  persistedSemaforiConfig,
  onSemaforiConfigChange,
  persistedLinksConfig,
  onLinksConfigChange,
  persistedVelocity,
  onVelocityChange,
}) {
  // Semafori list with editable parameters
  const [semaforiConfig, setSemaforiConfig] = useState([]);
  // Links between consecutive pairs
  const [linksConfig, setLinksConfig] = useState([]);
  // Global velocity (used as default for all links)
  const [velocity, setVelocity] = useState(persistedVelocity || 50);

  // Initialize semafori config when modal opens or selected lights change
  useEffect(() => {
    if (visible && selectedLights && selectedLights.length > 0) {
      // SMART MERGE: Build config by matching IDs from persisted config when possible
      const mergedConfig = selectedLights.map((lightId, index) => {
        // 1. Try to find existing config for this specific light ID in persisted state
        const existingConfig = persistedSemaforiConfig?.find(item => item.id === lightId);

        if (existingConfig) {
          // Preserve all existing settings for this light
          return { ...existingConfig };
        }

        // 2. If not found in persistence, find light details from available trafficLights
        const light = trafficLights?.find(l => l.id === lightId);

        // Determine coordinates
        let coords = [0, 0];
        if (light) {
          if (light.coordinates) coords = light.coordinates;
          else if (light.lat !== undefined && (light.lon !== undefined || light.lng !== undefined)) {
            coords = [light.lat, light.lon || light.lng];
          }
        }

        // 3. Create default config for a new light
        return {
          id: lightId,
          nome: light?.tags?.name || (light?.nome) || `Semaforo ${index + 1}`,
          // Visible and API fields
          green_start: 0,
          durata_verde: 30,
          yellow_time: 3,
          red_time: 30,
          lunghezza_attraversamento: 10,
          distanza: index === 0 ? 0 : 500, // Distance from start or previous
          coordinates: coords,
          client_type: getClientType(),
          reference: index === 0, // Default first light as reference
        };
      });

      setSemaforiConfig(mergedConfig);

      // Links config matching (consecutive pairs)
      // Note: linksConfig in this component uses fromId/toId format for UI but handleCalculate maps it for API
      const mergedLinks = [];
      for (let i = 0; i < mergedConfig.length - 1; i++) {
        const idA = mergedConfig[i].id;
        const idB = mergedConfig[i + 1].id;

        // Try to find if this exact link was already configured
        const existingLink = persistedLinksConfig?.find(l =>
          (l.fromId === idA && l.toId === idB) || (l.id_A === idA && l.id_B === idB)
        );

        if (existingLink) {
          mergedLinks.push({
            fromId: idA,
            fromNome: mergedConfig[i].nome,
            toId: idB,
            toNome: mergedConfig[i + 1].nome,
            distanza: existingLink.distanza || existingLink.distanza_A_B || 500,
            velocita: existingLink.velocita || persistedVelocity || 50
          });
        } else {
          mergedLinks.push({
            fromId: idA,
            fromNome: mergedConfig[i].nome,
            toId: idB,
            toNome: mergedConfig[i + 1].nome,
            distanza: 500,
            velocita: persistedVelocity || 50
          });
        }
      }
      setLinksConfig(mergedLinks);

      if (persistedVelocity) {
        setVelocity(persistedVelocity);
      }
    }
  }, [visible, selectedLights, trafficLights, persistedSemaforiConfig, persistedLinksConfig, persistedVelocity]);

  // Handle semaforo field change
  const handleSemaforoChange = (index, field, value) => {
    setSemaforiConfig(prev => {
      let updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Handle link field change
  const handleLinkChange = (index, field, value) => {
    setLinksConfig(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });

    // When link distance changes, sync to the target semaforo's distanza field
    if (field === 'distanza') {
      const link = linksConfig[index];
      if (link) {
        setSemaforiConfig(prev => {
          const updated = [...prev];
          const targetIdx = updated.findIndex(s => s.id === link.toId);
          if (targetIdx >= 0) {
            updated[targetIdx] = { ...updated[targetIdx], distanza: value };
          }
          return updated;
        });
      }
    }
  };

  // Handle velocity change – update all links
  const handleVelocityChange = (val) => {
    setVelocity(val);
    setLinksConfig(prev => prev.map(l => ({ ...l, velocita: val })));
  };

  // Remove semaforo from list
  const handleRemoveSemaforo = (index) => {
    if (semaforiConfig.length <= 1) {
      message.warning('Devi avere almeno un semaforo');
      return;
    }
    const newConfig = semaforiConfig.filter((_, i) => i !== index);
    setSemaforiConfig(newConfig);

    // Rebuild links
    const newLinks = [];
    for (let i = 0; i < newConfig.length - 1; i++) {
      newLinks.push({
        fromId: newConfig[i].id,
        fromNome: newConfig[i].nome,
        toId: newConfig[i + 1].id,
        toNome: newConfig[i + 1].nome,
        distanza: newConfig[i + 1].distanza || 100,
        velocita: velocity,
      });
    }
    setLinksConfig(newLinks);
  };

  // Add new semaforo
  const handleAddSemaforo = () => {
    const newSemaforo = {
      id: `new-${Date.now()}`,
      nome: '',
      green_start: 0,
      durata_verde: 30,
      yellow_time: 3,
      red_time: 30,
      lunghezza_attraversamento: 10,
      distanza: 100,
      coordinates: [0, 0],
      client_type: getClientType(),
      reference: false,
    };
    const newConfig = [...semaforiConfig, newSemaforo];
    setSemaforiConfig(newConfig);

    // Add link from last existing to new
    if (semaforiConfig.length > 0) {
      const last = semaforiConfig[semaforiConfig.length - 1];
      setLinksConfig(prev => [
        ...prev,
        {
          fromId: last.id,
          fromNome: last.nome,
          toId: newSemaforo.id,
          toNome: newSemaforo.nome,
          distanza: 100,
          velocita: velocity,
        },
      ]);
    }
  };

  // Handle calculate button – builds the API request body
  const handleCalculate = () => {
    if (semaforiConfig.length < 2) {
      message.warning('Seleziona almeno 2 semafori');
      return;
    }

    // Persist config in parent state
    if (onSemaforiConfigChange) onSemaforiConfigChange(semaforiConfig);
    if (onLinksConfigChange) onLinksConfigChange(linksConfig);
    if (onVelocityChange) onVelocityChange(velocity);

    // Build API request body matching new /api/calcola_onda_verde schema
    const requestBody = {
      signals: semaforiConfig.map((s, index) => {
        return {
          id: s.id,
          nome: s.nome || s.id,
          green_start: s.green_start || 0,
          green_duration: s.durata_verde || 30,
          yellow_time: s.yellow_time || 3,
          red_time: s.red_time || 30,
          crossing_length_m: s.lunghezza_attraversamento || 10,
          distanza: s.distanza, // Keeping for frontend backward compatibility
          coordinates: s.coordinates,
        };
      }),
      links: linksConfig.map((l) => {
        return {
          from_signal: l.fromId,
          to_signal: l.toId,
          distance_m: l.distanza,
          speed_kmh: l.velocita,
        };
      })
    };

    if (onCalculate) {
      onCalculate(requestBody);
    }
  };

  // Persist config when closing (so reopening restores values)
  const handleClose = () => {
    if (onSemaforiConfigChange) onSemaforiConfigChange(semaforiConfig);
    if (onLinksConfigChange) onLinksConfigChange(linksConfig);
    if (onVelocityChange) onVelocityChange(velocity);
    onClose();
  };

  return (
    <Modal
      title="Configura Semafori"
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={1100}
      className="tlgw-config-modal"
      centered

    >
      {/* Semafori Section */}
      <div className="tlgw-config-section">
        <div className="tlgw-config-section-header">
          <h3 className="tlgw-config-section-title">Informazioni Semafori</h3>
          <button
            className="tlgw-config-add-btn"
            onClick={handleAddSemaforo}
            title="Aggiungi semaforo"
          >
            <PlusOutlined />
          </button>
        </div>

        {/* Scrollable Table Container */}
        <div className="tlgw-config-table-container">
          {/* Table Header */}
          <div className="tlgw-config-table-header semafori">
            <div className="tlgw-config-col tlgw-config-col-nome">Nome*</div>
            <div className="tlgw-config-col tlgw-config-col-number">Inizio Verde [s]</div>
            <div className="tlgw-config-col tlgw-config-col-number">Durata Verde [s]</div>
            <div className="tlgw-config-col tlgw-config-col-number">Durata Giallo [s]</div>
            <div className="tlgw-config-col tlgw-config-col-number">Durata Rosso [s]</div>
            <div className="tlgw-config-col tlgw-config-col-number">Lungh. Attr. [m]</div>
            <div className="tlgw-config-col tlgw-config-col-action"></div>
          </div>

          {/* Semafori Rows */}
          <div className="tlgw-config-table-body">
            {semaforiConfig.map((semaforo, index) => (
              <div key={semaforo.id} className="tlgw-config-table-row semafori">
                <div className="tlgw-config-col tlgw-config-col-nome">
                  <input
                    type="text"
                    value={semaforo.nome}
                    onChange={(e) => handleSemaforoChange(index, 'nome', e.target.value)}
                    className="tlgw-config-text-input"
                    placeholder="ID semaforo"
                  />
                </div>
                <div className="tlgw-config-col tlgw-config-col-number">
                  <InputNumber
                    value={semaforo.green_start}
                    onChange={(val) => handleSemaforoChange(index, 'green_start', val)}
                    min={0}
                    className="tlgw-config-input-small"
                  />
                </div>
                <div className="tlgw-config-col tlgw-config-col-number">
                  <InputNumber
                    value={semaforo.durata_verde}
                    onChange={(val) => handleSemaforoChange(index, 'durata_verde', val)}
                    min={0}
                    className="tlgw-config-input-small"
                  />
                </div>
                <div className="tlgw-config-col tlgw-config-col-number">
                  <InputNumber
                    value={semaforo.yellow_time}
                    onChange={(val) => handleSemaforoChange(index, 'yellow_time', val)}
                    min={0}
                    className="tlgw-config-input-small"
                  />
                </div>
                <div className="tlgw-config-col tlgw-config-col-number">
                  <InputNumber
                    value={semaforo.red_time}
                    onChange={(val) => handleSemaforoChange(index, 'red_time', val)}
                    min={0}
                    className="tlgw-config-input-small"
                  />
                </div>
                <div className="tlgw-config-col tlgw-config-col-number">
                  <InputNumber
                    value={semaforo.lunghezza_attraversamento}
                    onChange={(val) => handleSemaforoChange(index, 'lunghezza_attraversamento', val)}
                    min={0}
                    className="tlgw-config-input-small"
                  />
                </div>
                <div className="tlgw-config-col tlgw-config-col-action">
                  <button
                    className="tlgw-config-remove-btn"
                    onClick={() => handleRemoveSemaforo(index)}
                    title="Rimuovi"
                  >
                    <DeleteOutlined />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Link Pairs Section */}
      {linksConfig.length > 0 && (
        <div className="tlgw-config-section">
          <div className="tlgw-config-section-header">
            <h3 className="tlgw-config-section-title">Informazioni Tratte</h3>
          </div>

          <div className="tlgw-config-table-container">
            <div className="tlgw-config-table-header links">
              <div className="tlgw-config-col">Tratta</div>
              <div className="tlgw-config-col tlgw-config-col-number">Distanza [m]</div>
              <div className="tlgw-config-col tlgw-config-col-number">Velocità [km/h]</div>
            </div>

            <div className="tlgw-config-table-body">
              {linksConfig.map((link, index) => (
                <div key={`link-${index}`} className="tlgw-config-table-row links">
                  <div className="tlgw-config-col">
                    <span className="tlgw-link-label">
                      {link.fromNome || link.fromId} → {link.toNome || link.toId}
                    </span>
                  </div>
                  <div className="tlgw-config-col tlgw-config-col-number">
                    <InputNumber
                      value={link.distanza}
                      onChange={(val) => handleLinkChange(index, 'distanza', val)}
                      min={0}
                      className="tlgw-config-input-small"
                    />
                  </div>
                  <div className="tlgw-config-col tlgw-config-col-number">
                    <InputNumber
                      value={link.velocita}
                      onChange={(val) => handleLinkChange(index, 'velocita', val)}
                      min={1}
                      max={200}
                      className="tlgw-config-input-small"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="tlgw-config-actions">
        <Button
          size="large"
          className="tlgw-config-cancel-btn"
          onClick={handleClose}
        >
          Annulla
        </Button>
        <Button
          type="primary"
          size="large"
          className="tlgw-config-submit-btn"
          onClick={handleCalculate}
          loading={isLoading}
        >
          Calcola l'offset
        </Button>
      </div>
    </Modal>
  );
}

export default ConfigureSemaforiModal;
